import { useEffect } from 'react'

import { useKeycloak } from 'contexts/keycloak'
import { useMe } from 'hooks/use-me'
import { initializeIntercom } from 'utils/gtm'

import type { TAuthGateState } from './auth-gate.types'

/**
 * Owns the auth-boundary decision, handing the view a ready-to-render `TAuthGateState`.
 *
 * The `me` query, its on-login side effects and union decoding live in `hooks/use-me`; AuthGate
 * only orchestrates: Keycloak readiness → loading; ME error / `StatusMessage` → error; otherwise
 * render children behind `AuthContextProvider` (with `me: undefined` for anonymous users — public/
 * guest routes are `PrivateRoute`'s job).
 */
export const useAuthGate = (): TAuthGateState => {
  const { keycloak, initialized } = useKeycloak()
  const authenticated = Boolean(keycloak.authenticated)

  const { data, loading, error, hasStatusMessage } = useMe({ authenticated })

  // Anonymous path has no `me` query (skipped), so its `onCompleted` never runs — sync Intercom
  // with the keycloak-init outcome once it settles. The authenticated path's GTM/persistence runs
  // inside `useMe`'s `onCompleted` instead.
  useEffect(
    function initializeIntercomForAnonymousUser() {
      if (initialized && !authenticated) {
        initializeIntercom()
      }
    },
    [initialized, authenticated]
  )

  // Only block on the INITIAL load (no data yet). `loading` is also true during background refetches
  // (the query builder sets `notifyOnNetworkStatusChange: true`); reverting to the full-screen loader
  // on every refetch would unmount the whole App, which remounts and re-triggers the query — an
  // infinite loop. Once `data` has landed, stay `ready` through refetches.
  if (!initialized || (authenticated && loading && !data)) return { status: 'loading' }
  if (error || hasStatusMessage) return { status: 'error' }

  return { status: 'ready', keycloak, me: data }
}
